import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Translate } from 'react-localize-redux';
import {
  dimensionsPermission, hasSomePermission, manageSurveyPermission, summaryPermission
} from 'unihow-common';
import {
  getCompiledPath, getExtraSurveyQuestions, getSubRouteForSurveyType, getSurveyQuestions, isNumericQuestion,
  sortVariableStatisticsByType
} from '../../../util';
import { getTranslation, langType, withLang } from '../../../i18n';
import VariableSummaryChart from '../../VariableSummaryChart';
import SurveyQuestionSummary from '../../SurveyQuestionSummary';
import styles from '../surveyStatistics.module.css';
import { surveyTypeValues } from '../../../constants/types';
import useCurrentUser from '../../../hooks/useCurrentUser';

const Summary = ({
  params,
  surveyInfo,
  surveyStats,
  surveyType,
  surveySchedule,
  lang,
  translate: t
}) => {
  const { permissions: userPermissions } = useCurrentUser();
  const getSurveyVariables = () => {
    const variableStatistics = sortVariableStatisticsByType(surveyStats.variableStatistics);
    const { surveyId } = params;
    const surveyTypeRoute = getSubRouteForSurveyType(surveyType);
    return (
      <div className={styles.container}>
        <h2><Translate id="surveyStatistics.dimensions" /></h2>
        {variableStatistics.map((v, i) => (
          <div className={styles.variableContainer} key={getTranslation(v.name, lang)}>
            <h3 className={styles.variableHeader}>{getTranslation(v.name, lang)}</h3>
            <div className={styles.chartContainer}>
              <VariableSummaryChart
                stats={v}
                lang={lang}
                translate={t}
                showStandardDeviation
                label={getTranslation(v.name, lang)}
              />
              <div className={styles.distributionLinkRow}>
                <Link to={getCompiledPath(surveyTypeRoute.survey.dimension,
                  { surveyId, dimensionId: i })}
                >{`${t('surveyStatistics.responseDistribution')} »`}
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getOtherAnswers = (isFirstSection) => (
    <>
      <h2><Translate id={isFirstSection ? 'surveyStatistics.answerDistributions' : 'surveyStatistics.otherAnswers'} /></h2>
      <SurveyQuestionSummary surveyStats={surveyStats} />
    </>
  );

  const getExtraAnswers = () => (
    <>
      <h2><Translate id="surveyStatistics.extraQuestions" /></h2>
      <SurveyQuestionSummary surveyStats={surveyStats} extraQuestions />
    </>
  );

  const getVariableAndQuestionSummary = ({ variableStatistics, survey }) => {
    const variableQuestionCodes = (variableStatistics || [])
      .reduce((codes, varStat) => codes.concat(varStat.questionCodes), []);
    const nonVariableQuestions = getSurveyQuestions(survey).filter((question) =>
      !variableQuestionCodes.includes(question.code) && isNumericQuestion(question));
    const extraQuestions = getExtraSurveyQuestions(survey).filter(isNumericQuestion);
    return (
      <>
        {(variableStatistics && hasSomePermission(userPermissions, [
          manageSurveyPermission(surveyType),
          dimensionsPermission(surveyType),
          summaryPermission(surveyType)
        ])) ? getSurveyVariables() : null}
        {nonVariableQuestions.length ? getOtherAnswers(!variableStatistics) : null}
        {extraQuestions.length ? getExtraAnswers() : null}
      </>
    );
  };

  if (surveyStats && surveyStats.computingStatistics) {
    return <h3><Translate id="surveyStatistics.computingStatistics" /></h3>;
  } if (surveyInfo && !surveyInfo.hasEnoughResponses) {
    return <h3><Translate id="surveyStatistics.notEnoughResponses" /></h3>;
  } if (surveySchedule && !surveySchedule.ended) {
    return <h3><Translate id="surveyStatistics.noStatsUntilClosed" /></h3>;
  } if (surveyStats) {
    return getVariableAndQuestionSummary(surveyStats);
  }
  return null;
};

const {
  shape,
  string,
  oneOf
} = PropTypes;

Summary.propTypes = {
  params: shape({
    surveyId: string
  }).isRequired,
  surveyInfo: shape(),
  surveyStats: shape(),
  surveySchedule: shape(),
  surveyType: oneOf(Object.values(surveyTypeValues)).isRequired,
  ...langType
};

Summary.defaultProps = {
  surveyInfo: null,
  surveyStats: null,
  surveySchedule: null
};

export default withLang(Summary);
